import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { ukDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Merge, AlertTriangle, Undo2, Sparkles } from "lucide-react";

const cell = "w-full bg-transparent border border-transparent hover:border-stone-200 focus:border-stone-400 rounded px-1.5 py-1 text-sm outline-none";
const blankRow = () => ({ row_id: crypto.randomUUID(), kind: "service", category: "", name: "", duration_min: 60, duration_missing: true, price: "", price_type: "fixed", description: "", notes: "", addon_for: "", confidence: 1, flags: ["Duration missing - set to 60 min, please check"] });

function ReviewTable({ rows, setRows, selected, setSelected }) {
  const upd = (id, k, v) => setRows((rs) => rs.map((r) => (r.row_id === id ? { ...r, [k]: v, ...(k === "duration_min" ? { duration_missing: false, flags: r.flags.filter((f) => !f.startsWith("Duration")) } : {}), ...(k === "price" && v !== "" ? { flags: r.flags.filter((f) => f !== "Price unclear") } : {}) } : r)));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="overflow-x-auto border border-stone-200 rounded-xl">
      <table className="w-full text-sm min-w-[980px]" data-testid="import-review-table">
        <thead className="bg-stone-50 text-xs text-stone-500 text-left">
          <tr><th className="p-2 w-8" /><th className="p-2 w-24">Type</th><th className="p-2 w-44">Category</th><th className="p-2">Service</th><th className="p-2 w-20">Mins</th><th className="p-2 w-24">Price £</th><th className="p-2 w-24">Price type</th><th className="p-2 w-52">Notes</th><th className="p-2 w-8" /></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.row_id} data-testid={`import-row-${i}`} className={`border-t border-stone-100 align-top ${r.flags.length ? "bg-amber-50" : ""}`}>
              <td className="p-2"><Checkbox checked={selected.includes(r.row_id)} onCheckedChange={() => toggle(r.row_id)} data-testid={`import-row-select-${i}`} /></td>
              <td className="p-1"><select value={r.kind} onChange={(e) => upd(r.row_id, "kind", e.target.value)} className={cell}><option value="service">Service</option><option value="addon">Add-on</option><option value="extra">Extra</option></select></td>
              <td className="p-1"><input value={r.category} onChange={(e) => upd(r.row_id, "category", e.target.value)} className={cell} /></td>
              <td className="p-1">
                <input data-testid={`import-row-name-${i}`} value={r.name} onChange={(e) => upd(r.row_id, "name", e.target.value)} className={`${cell} font-medium`} />
                {r.flags.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{r.flags.map((f) => <span key={f} className="text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{f}</span>)}</div>}
              </td>
              <td className="p-1"><input type="number" data-testid={`import-row-duration-${i}`} value={r.duration_min} onChange={(e) => upd(r.row_id, "duration_min", Number(e.target.value))} className={`${cell} font-mono ${r.duration_missing ? "text-amber-700" : ""}`} /></td>
              <td className="p-1"><input type="number" step="0.01" data-testid={`import-row-price-${i}`} value={r.price ?? ""} onChange={(e) => upd(r.row_id, "price", e.target.value)} className={`${cell} font-mono`} /></td>
              <td className="p-1"><select value={r.price_type} onChange={(e) => upd(r.row_id, "price_type", e.target.value)} className={cell}><option value="fixed">Fixed</option><option value="from">From</option></select></td>
              <td className="p-1"><textarea value={[r.description, r.notes].filter(Boolean).join(" ")} onChange={(e) => { upd(r.row_id, "description", e.target.value); upd(r.row_id, "notes", ""); }} rows={1} className={`${cell} resize-y`} /></td>
              <td className="p-2"><button data-testid={`import-row-delete-${i}`} onClick={() => setRows((rs) => rs.filter((x) => x.row_id !== r.row_id))} className="text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function History({ items, onUndo }) {
  const done = items.filter((i) => ["committed", "undone"].includes(i.status));
  if (!done.length) return null;
  return (
    <div className="mt-6">
      <h3 className="font-semibold text-sm mb-2">Previous imports</h3>
      <div className="space-y-2">
        {done.map((i) => (
          <div key={i.id} data-testid={`import-history-${i.id}`} className="flex items-center justify-between border border-stone-200 rounded-lg px-3 py-2 text-sm">
            <div><span className="font-medium">{(i.files || []).join(", ")}</span><span className="text-stone-500"> · {ukDate(i.committed_at || i.created_at)} · {(i.created_service_ids || []).length} services</span></div>
            {i.status === "undone" ? <span className="text-xs text-stone-400">Undone</span> : i.can_undo ? (
              <Button size="sm" variant="outline" className="rounded-full" data-testid={`import-undo-${i.id}`} onClick={() => onUndo(i.id)}><Undo2 className="h-3.5 w-3.5 mr-1" />Undo (until {ukDate(i.undo_until)})</Button>
            ) : <span className="text-xs text-stone-400">Undo expired</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function PriceListImport({ open, onOpenChange, onImported }) {
  const [stage, setStage] = useState("upload");
  const [imp, setImp] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [applyInfo, setApplyInfo] = useState(false);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = useCallback(async () => { const { data } = await api.get("/imports"); setHistory(data); }, []);
  useEffect(() => { if (open) { setStage("upload"); setImp(null); setRows([]); setSelected([]); loadHistory(); } }, [open, loadHistory]);

  useEffect(() => {
    if (stage !== "processing" || !imp) return;
    const t = setInterval(async () => {
      const { data } = await api.get(`/imports/${imp.id}`);
      if (data.status === "review") { setImp(data); setRows(data.rows); setStage("review"); }
      if (data.status === "failed") { toast.error("We couldn't read those files. Try clearer photos."); setStage("upload"); }
    }, 3000);
    return () => clearInterval(t);
  }, [stage, imp]);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try { const { data } = await api.post("/imports/scan", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImp(data); setStage("processing"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const merge = () => {
    const sel = rows.filter((r) => selected.includes(r.row_id));
    if (sel.length < 2) return toast.error("Select 2 or more rows to merge");
    const [first, ...rest] = sel;
    const merged = { ...first, description: [first.description, first.notes, ...rest.map((r) => [r.name, r.description, r.notes].filter(Boolean).join(": "))].filter(Boolean).join(" · "), notes: "" };
    setRows((rs) => rs.filter((r) => !rest.some((x) => x.row_id === r.row_id)).map((r) => (r.row_id === first.row_id ? merged : r)));
    setSelected([]);
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${imp.id}/commit`, { rows: rows.map((r) => ({ ...r, price: r.price === "" ? null : Number(r.price) })), apply_business_info: applyInfo });
      toast.success(`Imported ${data.created} services`); onImported?.(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const undo = async (id) => {
    if (!window.confirm("Remove all services created by this import?")) return;
    try { const { data } = await api.post(`/imports/${id}/undo`); toast.success(`Removed ${data.removed} services`); loadHistory(); onImported?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const flagged = rows.filter((r) => r.flags.length).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="price-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#B07E4F]" />Upload your price list</DialogTitle></DialogHeader>
        {stage === "upload" && (
          <>
            <label className="block border-2 border-dashed border-stone-300 rounded-2xl p-10 text-center cursor-pointer hover:bg-stone-50">
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,image/*,application/pdf" data-testid="price-import-file-input" className="hidden" onChange={onFiles} />
              <Upload className="h-8 w-8 mx-auto text-stone-400" />
              <div className="mt-3 font-semibold">Drop in photos, screenshots or PDFs</div>
              <p className="text-sm text-stone-500 mt-1">PDF, JPG, PNG, HEIC — several files at once. We'll read your services, prices and durations. Nothing goes live until you check and confirm.</p>
            </label>
            <History items={history} onUndo={undo} />
          </>
        )}
        {stage === "processing" && (
          <div className="py-16 text-center" data-testid="price-import-processing">
            <div className="h-10 w-10 rounded-full border-2 border-stone-300 border-t-stone-800 animate-spin mx-auto" />
            <p className="mt-4 font-medium">Reading your price list…</p><p className="text-sm text-stone-500">This usually takes 20–60 seconds.</p>
          </div>
        )}
        {stage === "review" && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-stone-600"><b>{rows.length}</b> rows found{flagged > 0 && <> · <span className="text-amber-700">{flagged} need checking (amber)</span></>}. Edit anything, then import.</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" data-testid="import-merge" onClick={merge}><Merge className="h-3.5 w-3.5 mr-1" />Merge selected</Button>
                <Button size="sm" variant="outline" className="rounded-full" data-testid="import-add-row" onClick={() => setRows((rs) => [...rs, blankRow()])}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>
              </div>
            </div>
            <ReviewTable rows={rows} setRows={setRows} selected={selected} setSelected={setSelected} />
            <p className="text-xs text-stone-500">Add-ons are bookable alongside services in their category. Extras (surcharges) are saved for staff to add at checkout and aren't bookable online.</p>
            {(imp?.contact?.phone || imp?.opening_hours?.text || imp?.promotions?.length > 0) && (
              <div className="rounded-xl border border-stone-200 p-4 text-sm space-y-1" data-testid="import-business-info">
                <div className="font-semibold">Also found</div>
                {imp.opening_hours?.text && <div><span className="text-stone-500">Hours:</span> {imp.opening_hours.text}</div>}
                {(imp.contact?.phone || imp.contact?.address) && <div><span className="text-stone-500">Contact:</span> {[imp.contact.phone, imp.contact.email, imp.contact.address].filter(Boolean).join(" · ")}</div>}
                {imp.promotions?.map((p) => <div key={p.name}><span className="text-stone-500">Promotion:</span> {p.name} — {p.details} <span className="text-xs text-stone-400">(set up in Settings → Promotions)</span></div>)}
                <label className="flex items-center gap-2 pt-2"><Checkbox checked={applyInfo} onCheckedChange={(v) => setApplyInfo(!!v)} data-testid="import-apply-info" />Update my opening hours & contact details with these</label>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="rounded-full" onClick={async () => { await api.delete(`/imports/${imp.id}`); setStage("upload"); loadHistory(); }}>Discard</Button>
              <Button data-testid="import-commit" disabled={busy || rows.length === 0} onClick={commit} className="rounded-full bg-stone-900 hover:bg-stone-800">{busy ? "Importing…" : `Import ${rows.length} rows`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
